'use client';

/** CRM — leads across all businesses, /api/hub/crm/all/leads (same hub fan-out as classic). */
import { useEffect, useState } from 'react';
import { Glass, Badge, StatusChip, HoloTable, Row, EmptyState, mono, display } from '@/future/primitives';

const BADGE_HUES = ['#ffb454', '#7db4ff', '#b49aff', '#ff9ad5', '#5df2b4', '#e6c88a', '#f2a0b5', '#9ef0ff'];
const hue = (k: string) => BADGE_HUES[[...(k || 'x')].reduce((s, c) => s + c.charCodeAt(0), 0) % BADGE_HUES.length];

export function CrmSurface() {
  const [leads, setLeads] = useState<any[]>([]);
  const [open, setOpen] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    const load = () => fetch('/api/hub/crm/all/leads?limit=200')
      .then(r => r.json())
      .then(d => { if (live) { setLeads(d?.leads || []); setLoading(false); } })
      .catch(() => setLoading(false));
    load();
    const i = setInterval(load, 2 * 60_000); // live-stats law: 2min
    return () => { live = false; clearInterval(i); };
  }, []);

  return (
    <div className="max-w-[1240px] mx-auto space-y-4" style={{ ['--uif-cols' as any]: '1.2fr 1.6fr 1fr .9fr 2.2fr .7fr' }}>
      <div className="flex items-center">
        <span style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{leads.length} LEADS · ALL BUSINESSES · LIVE 2-MIN SYNC</span>
        {/* hub deep-link law (classic parity): Beast is the cockpit, the hub is the engine */}
        <a href="https://app.anydomain.ca/crm" target="_blank" rel="noopener noreferrer" className="ml-auto"
          style={{ ...mono(15, '.2em'), color: 'var(--uif-cy-interactive)', border: '1px solid rgba(125,232,255,.25)', borderRadius: 999, padding: '5px 12px', textDecoration: 'none' }}>OPEN HUB ↗</a>
      </div>
      <Glass className="uif-scanlines relative overflow-hidden" style={{ borderRadius: 'var(--uif-radius)' }}>
        <div className="py-3">
          <HoloTable head={['LEAD', 'CONTACT', 'BUSINESS', 'CITY', 'MESSAGE', 'WHEN']}>
            {leads.map((l: any) => (
              <Row key={`${l.site}-${l.id}`} cols={6} onOpen={() => setOpen(l)}>
                <div style={{ ...display(25, 400), color: 'var(--uif-t1)' }}>{l.name || '—'}</div>
                <div>
                  <div style={{ ...mono(15), color: 'var(--uif-t4)', textTransform: 'none' }}>{l.email}</div>
                  {l.phone && <div style={{ ...mono(14), color: 'var(--uif-t6)' }}>{l.phone}</div>}
                </div>
                <div><Badge label={l.site || '—'} color={hue(l.site)} /></div>
                <div style={{ ...display(23, 300), color: 'var(--uif-t4)' }}>{l.city || '—'}</div>
                <div className="truncate" style={{ ...display(23, 300), color: 'var(--uif-t5)' }}>{l.message || ''}</div>
                <div style={{ ...mono(14), color: 'var(--uif-t6)' }}>{l.created_at ? new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
              </Row>
            ))}
          </HoloTable>
          {!loading && leads.length === 0 && <EmptyState what="leads" />}
          {loading && <div className="py-12 text-center" style={{ ...mono(17, '.3em'), color: 'var(--uif-t5)' }}>SYNCING LEADS…</div>}
        </div>
      </Glass>
      {open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: 'var(--uif-backdrop)', backdropFilter: 'blur(6px)' }}
          onMouseUp={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="w-[620px] rounded-2xl p-8" style={{ background: 'var(--uif-slab-bg)', border: '1px solid var(--uif-slab-border)', boxShadow: 'var(--uif-slab-shadow)' }}>
            <div className="h-[2px] w-full mb-5" style={{ background: `linear-gradient(90deg, ${hue(open.site)}, transparent 70%)` }} />
            <div className="flex items-center gap-3 mb-2">
              <Badge label={open.site} color={hue(open.site)} />
              <StatusChip label="LEAD" kind="info" />
            </div>
            <h2 style={{ ...display(48, 200), color: 'var(--uif-t1)' }}>{open.name}</h2>
            <div className="mt-1 mb-4" style={{ ...mono(17, '.08em'), color: 'var(--uif-t4)', textTransform: 'none' }}>{open.email} {open.phone ? `· ${open.phone}` : ''} {open.city ? `· ${open.city}` : ''}</div>
            <div style={{ ...display(27, 300), lineHeight: 1.75, color: 'var(--uif-t-body)' }}>{open.message}</div>
            <div className="mt-6 flex justify-end">
              <a href={`https://app.anydomain.ca/crm/${open.site || ''}`} target="_blank" rel="noopener noreferrer"
                style={{ ...mono(15, '.16em'), color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 14px rgba(0,240,255,.35)', borderRadius: 999, padding: '7px 16px', textDecoration: 'none' }}>REPLY IN HUB ↗</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
